<script lang="ts">
    
    import { getContext, onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import GameMetaInfo from "../../Components/Game/GameMetaInfo.svelte";
    import Button from "../../Components/Manager/Button.svelte";
    import Menu from "../../Components/Manager/Menu.svelte";
    import RoomBar from "../../Components/Rooms/RoomBar.svelte";
    import RoomLog from "../../Components/Rooms/RoomLog.svelte";
    import RoomPlayersList from "../../Components/Rooms/RoomPlayersList.svelte";
    import TopBar from "../../Components/Shared/Mobile/TopBar.svelte";
    import WaitingClock from "../../Components/WaitingClock.svelte";
    import Watch from "../../Components/Watch.svelte";
    import type { Fn } from "../../global";
    import type { Game } from "../../Model/Game";
    import { authorizationService, gamesService, monitorScreenService, roomsService } from "../../Services";
    import { i18n } from "../../Services/i18n";
    import RightMenuBtn from "./RightMenuBtn.svelte";
    import type { Room } from "../../Model/Room";
    import type { EventsChannel } from "../../Services/EventsChannel";
    import { FormGroup, Label, Modal, ModalBody, ModalHeader } from "sveltestrap";

    const getGame: () => Game = getContext('getGame');
    const clearGame: () => void = getContext('clearGame');
    const toggleRoomSettingsModal: Fn = getContext('toggleRoomSettingsModal');
    const game = getGame();

    let isCanceling = false;
    let openRightMenu = false;
    let openEndGameModal: boolean = false;
    let cancelMotive = null;
    let playersCount: number = 0;
    let room: Room;
    let roomEventsChannel: EventsChannel;
    let showCancelOptions: boolean = false;

    onMount(async () => {
        try {
            room = await roomsService.getRoom(game.roomId);
            roomEventsChannel = room.getEventsChannel();
            roomEventsChannel.on('updated', onUpdated);
        } catch(error) {
            console.log(error)
        }
    })

    function onUpdated() {
        location.reload();
    }

    async function onCancel(motive: string): Promise<boolean> {
        if (isCanceling || !confirm($i18n("¿Seguro que desea cancelar el juego actual?"))) {
            return false;
        }
        isCanceling = true;
        try {
            cancelMotive = motive;
            await gamesService.cancelGame(game.roomId, game.id, cancelMotive);
            toggleRightMenu();
            clearGame();
            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            isCanceling = false;
        }
    }

    async function onGameStart(): Promise<void> {
        if (game.getTimeToStart() > 0 && !confirm($i18n("¿Seguro que desea empezar el juego antes de que termine la cuenta atrás?"))) {
            return;
        }
        try {
            await gamesService.startGame(game.roomId, game.id);
            game.state.set('playing');
        } catch (error) {
            console.error(error);
        }
    }

    function toggleRightMenu(): void {
        openRightMenu = !openRightMenu;
    }

    async function onLogout(): Promise<void> {
        if (await onCancel('logout')) {
            authorizationService.clearToken();
            navigate('/');
        }
    }

    function openMonitor(): void {
        monitorScreenService.openMonitor(game.roomId);
        openRightMenu = false;
    }

    function toggleEndGameOptions(): void {
        showCancelOptions = !showCancelOptions;
        cancelMotive = null;
    }

    function toggleEndGameModal(): void {
        openEndGameModal = !openEndGameModal;
        cancelMotive = null;
    }

    async function toggleQr() {
        room.showRoomQrInMonitor.set(!room.showRoomQrInMonitor.value);
        await roomsService.saveRoom(room);
    }

</script>

<TopBar>
    <RightMenuBtn on:click={toggleRightMenu} />
</TopBar>

<div class="waiting-room">
    <div class="room-bar-container">
        <RoomBar roomId={game.roomId}>
            <GameMetaInfo {game} />
        </RoomBar>
    </div>

    <div class="players-list-container border p-3 rounded">
        <h6>{$i18n("Jugadores")}: {playersCount}</h6>
        <RoomPlayersList roomId={game.roomId} bind:playersCount />
    </div>

    <Watch state={game.waitingEnded} let:value={waitingEnded}>
        <Button on:click={onGameStart}>
            {#if waitingEnded}
                {$i18n("Pulsa para comenzar")}
            {:else}
                {$i18n("Comienza en")} <WaitingClock {game} />
            {/if}
        </Button>
    </Watch>

    <RoomLog roomId={game.roomId} />

</div>

<Menu isOpen={openRightMenu} toggle={toggleRightMenu} on:logout={onLogout}>

    <div class="d-flex gap-2 mb-3">
        <div class='posiyion-relative'>
            <Button on:click={toggleEndGameOptions} disabled={isCanceling}><!-- toggleEndGameModal -->
                {#if isCanceling}
                    {$i18n("Cancelando")}
                {:else}
                    {$i18n("Cancelar juego")}
                {/if}
            </Button>
            {#if showCancelOptions}
                <div class='bg-white position-absolute d-flex flex-column rounded-3' style='z-index: 20;'>
                    <button on:click={() => onCancel('training')} class='btn btn-light'>{$i18n("Formación")}</button>
                    <button on:click={() => onCancel('game_change')} class='btn btn-light'>{$i18n("Cambiar juego")}</button>
                    <button on:click={() => onCancel('network')} class='btn btn-light'>{$i18n("Internet")}</button>
                    <button on:click={() => onCancel('game_test')} class='btn btn-light'>{$i18n("Pruebas")}</button>
                    <button on:click={toggleEndGameModal} class='btn btn-light'>{$i18n("Otros")}</button>
                </div>
            {/if}
        </div>

        <Button on:click={openMonitor} class="flex-shrink-0">
            Monitor
        </Button>
    </div>

    <Button on:click={toggleRoomSettingsModal}>
        {$i18n("Ajustes de la sala")}
    </Button>

    {#if room}
        <div class="mt-3 notranslate">
            <Button on:click={toggleQr}>
                {room.showRoomQrInMonitor.value ? $i18n("Ocultar QR") : $i18n("Mostrar QR")}
            </Button>
        </div>
    {/if}
</Menu>

<Modal isOpen={openEndGameModal}>
    <ModalHeader toggle={toggleEndGameModal}>
        {$i18n("Cancelación de juego")}
    </ModalHeader>
    <ModalBody> 
        <form>
            <FormGroup>
                <Label>{$i18n("¿Por qué quieres cancelar este juego?")}</Label>
                <input type="text" class="form-control" bind:value={cancelMotive}>
            </FormGroup>
            <!-- <Button type="submit" disabled={!cancelMotive}>{$i18n("Cancelar juego")}</Button> -->
            <button class='btn btn-warning' type="submit" disabled={!cancelMotive} on:click={() => onCancel(cancelMotive)}>{$i18n("Cancelar juego")}</button>
        </form>
    </ModalBody>
</Modal>

<style lang="scss">

    .waiting-room {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
        margin-bottom: 10px;
    }

    .room-bar-container {
        margin: -1rem -1rem 0;
        font-family: ManagerTitle;
        font-size: 1.6rem;
    }

    .players-list-container {
        background-color: rgba(255, 255, 255, 0.4);
        flex: 1 1 0;
        overflow: auto;
    }

    .container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 90%;
        background-color: #fff;

        canvas {
            max-width: 100%;
        }
    }

    .game-code {
        margin: 2vw 0;

        .code {
            border: 0.4vw solid #00152E;
            color: #00152E;
            font-size: 5vw;
            padding: 0 2vw;
            border-radius: 1vw;
        }
    }

</style>


